/**
 * Shared contract for the compared-packages personalization trigger (WAT-11464).
 *
 * The package comparison tool writes this cookie as the customer builds their
 * comparison — on every change, NOT on exit; the server-side personalization
 * engine reads it to resolve variants. Both sides import from this module so
 * the wire format has exactly one owner and cannot drift.
 *
 * Writing on change does not personalize the page the customer is on (already
 * server-rendered), so AC1's "only after they leave" still holds. Waiting for
 * exit would be too late: `pagehide` fires only once the NEXT document commits
 * and a client navigation fetches its data before the old tree unmounts, so the
 * request the trigger evaluates would go out without the cookie.
 *
 * Cookie rather than localStorage (story AC9): the personalization engine runs
 * on the server and can only see cookies. See the deviation note on WAT-11464.
 */

/**
 * Cookie holding the customer's most recent comparison, by column.
 *
 * One letter apart from the `cmpPkg` QUERY PARAM, which is a different thing:
 * `?cmpPkg=a,b` is an inbound deep link naming packages to display, while
 * `cmpPkgs` is our outbound record of the columns the customer ended up with.
 */
export const COMPARED_PACKAGES_COOKIE = "cmpPkgs";

/** AC3 — up to three comparison columns are supported. */
export const MAX_COMPARED_COLUMNS = 3;

/**
 * Joins two package ids inside ONE CMS condition item to mean "compared A to B":
 * `ATT_TV_MYENTERTAINMENT+ATT_TV_MYNEWS` matches only when the two landed in
 * DIFFERENT comparison columns.
 *
 * It lives inside the item rather than in a separate "match mode" field so that
 * a single condition list can mix audiences — `["ATT_TV_MYSPORTS", "A+B"]` reads
 * as "compared MySports at all, OR compared A against B" — which a per-condition
 * mode flag could not express without a second variant.
 *
 * Safe as a separator because every package id is `[A-Z0-9_]+` (the same
 * invariant the cookie encoding relies on, documented below), so `+` can never
 * occur inside an id.
 */
export const COMPARISON_PAIR_SEPARATOR = "+";

/**
 * AC6/AC7 — the value must survive future visits with no expiry logic of our
 * own. 400 days is the longest lifetime browsers honour (Chrome caps cookie
 * expiry there), so this is "as close to never expires as a cookie can get".
 */
export const COMPARED_PACKAGES_LIFESPAN_DAYS = 400;

/**
 * Separators must satisfy three constraints at once:
 *  - legal RFC 6265 `cookie-octet` (`,` is NOT — 0x2C is the historical Set-Cookie
 *    list delimiter, and edge/CDN parsers split on it, truncating the value);
 *  - escaped by encodeURIComponent, so an id can never forge an extra column
 *    (which rules out `~ . ! * ' ( ) - _`, left untouched by the encoder);
 *  - never `=`, because `server/utils.ts` parseCookieStore splits each cookie on
 *    `=` and keeps index 1. encodeURIComponent emits `%3D`, so ids are safe.
 */
const COLUMN_SEPARATOR = "|";
const PACKAGE_SEPARATOR = "$";

/**
 * Package ids may contain a separator, so each id is percent-encoded.
 *
 * 🔴 That anti-forgery property holds on the PAGES router only. The App Router page
 * passes Next's `await cookies()` store straight through as `cookieStore`
 * (`app/v2/[...slug]/page.tsx:37,81`), and that parser already ran
 * `decodeURIComponent` on the value (`next/dist/compiled/@edge-runtime/cookies`).
 * So on App Router an id containing a raw separator arrives ALREADY decoded and is
 * split into extra columns before this module ever sees it — an idempotent decode
 * here cannot help, because the split happens first.
 *
 * Latent, not live: every real package id is `[A-Z0-9_]+` (ATT_TV_*, DTV_*), which
 * `encodeURIComponent` leaves untouched, so both routers agree today. The invariant
 * to preserve is therefore "ids contain no character encodeURIComponent escapes".
 * The structural fix is to build the personalization cookieStore from the raw
 * header rather than `cookies()`, which would also retire the dual-shape branch in
 * `getCookieValue` — out of scope here, raised on the PR.
 */
const encodeColumn = (column?: string[]): string => (Array.isArray(column) ? column : [])
  ?.filter?.(id => typeof id === "string" && id?.length > 0)
  ?.map?.(id => encodeURIComponent(id))
  ?.join?.(PACKAGE_SEPARATOR) ?? "";

const decodeColumn = (column?: string): string[] => (column ?? "")
  ?.split?.(PACKAGE_SEPARATOR)
  ?.filter?.(id => id?.length > 0)
  ?.map?.(id => decodeURIComponent(id)) ?? [];

/**
 * ponytail: hard ceiling instead of per-package trimming. Browsers silently
 * drop a cookie over ~4KB, and a silently-dropped cookie looks identical to
 * "customer never compared". Three columns of real package ids land near
 * ~200 bytes, so this only fires on absurd input. Trim per-column if a real
 * configuration ever approaches it.
 */
const MAX_SERIALIZED_LENGTH = 1024;

/**
 * Encode the comparison column structure for the cookie.
 *
 * Column position is significant, so empty columns are preserved rather than
 * dropped — `"a$b||c"` means column 1 holds a and b, column 2 is empty, and
 * column 3 holds c (AC12).
 *
 * @param columns Package ids per column, outer index = column.
 * @returns The cookie value, or `""` when there is nothing worth storing.
 */
export const serializeComparedPackages = (columns?: string[][], maxColumns = MAX_COMPARED_COLUMNS): string => {
  if (!Array.isArray(columns)) {
    return "";
  }

  // The caller passes the tool's CMS-configured column count; MAX_COMPARED_COLUMNS
  // is only the fallback for callers that have none.
  const cap = Number.isFinite(maxColumns) && maxColumns > 0 ?
    Math.floor(maxColumns) :
    MAX_COMPARED_COLUMNS;
  const encoded = columns
    ?.slice?.(0, cap)
    ?.map?.(encodeColumn)
    ?.join?.(COLUMN_SEPARATOR) ?? "";

  // Every column empty ("", "||", …) carries no signal — treat as no comparison.
  if (!encoded?.replace?.(/\|/g, "")?.length) {
    return "";
  }

  return encoded?.length > MAX_SERIALIZED_LENGTH ? "" : encoded;
};

/**
 * Decode the cookie back into the comparison column structure.
 *
 * The input is a cookie, so it is attacker-controllable: a hand-edited value
 * such as `"%"` makes decodeURIComponent throw. Malformed input degrades to an
 * empty comparison rather than breaking page render.
 *
 * Deliberately NOT capped by column count. The write side already caps columns
 * from the comparison tool's CMS-configured column count, so a second hardcoded
 * cap here could only ever DISAGREE with it: raise the CMS count to four and the
 * fourth column's packages would silently stop matching triggers, with nothing
 * to point at. What the customer put in their columns is what we read back; the
 * only bound that belongs at this layer is byte length, and that is enforced on
 * write (MAX_SERIALIZED_LENGTH) and by the browser's own ~4KB cookie ceiling.
 *
 * @param value Raw cookie value.
 * @returns Package ids per column, outer index = column. `[]` when absent or unparseable.
 */
export const parseComparedPackages = (value?: string): string[][] => {
  if (typeof value !== "string" || value?.length === 0) {
    return [];
  }

  // Not a column cap — a byte bound at a trust boundary. We never WRITE a value
  // longer than this, so anything longer was hand-edited, and every column in it
  // is work the request has to do. Rejecting it keeps a forged cookie from
  // handing the matcher thousands of columns.
  if (value?.length > MAX_SERIALIZED_LENGTH) {
    return [];
  }

  try {
    return value
      ?.split?.(COLUMN_SEPARATOR)
      ?.map?.(decodeColumn) ?? [];
  } catch {
    return [];
  }
};
